import { $generateHtmlFromNodes, $isRootTextContentEmpty, RichTextEditor } from '@talentmesh/rte';
import { Field, FieldDescription, FieldError, FieldLabel } from '@talentmesh/ui/components/field';
import { cn } from '@talentmesh/ui/lib/utils';
import { useEffect, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { ClientFormValues } from '../Client';
import useDebounce from '@/Hooks/UseDebounce';
import UIStrings from '@/Utils/UIStrings';

interface ClientDescriptionProps {
    initialHtml?: string | null;
}

const ClientDescription = ({ initialHtml }: ClientDescriptionProps) => {
    const {
        setValue,
        resetField,
        formState: { errors },
    } = useFormContext<ClientFormValues>();
    const { field } = useController<ClientFormValues>({ name: 'description' });
    const [description, setDescription] = useState('');
    const debouncedDescription = useDebounce(description, 50);

    useEffect(() => {
        setValue('description', debouncedDescription, {
            shouldValidate: true,
            shouldDirty: true,
        });
    }, [debouncedDescription, setValue, resetField]);

    return (
        <Field>
            <FieldLabel htmlFor="description" className="text-foreground">
                {UIStrings.CompanyDescription}
            </FieldLabel>
            <FieldDescription className="text-foreground">{UIStrings.CompanyDescriptionExplanation}</FieldDescription>
            <div className="relative pb-2">
                <RichTextEditor
                    initialHtml={initialHtml!}
                    features={['headingStyle', 'bold', 'italic', 'ol', 'ul']}
                    onChange={(editorState, editor) => {
                        editorState.read(() => {
                            // quickcheck the text content if rte is truly empty, because empty rte is <p><br><p>
                            if ($isRootTextContentEmpty(editor.isComposing())) {
                                setDescription('');
                            } else {
                                setDescription($generateHtmlFromNodes(editor));
                            }
                        });
                    }}
                    placeholder={
                        <div className="user-select-none pointer-events-none absolute top-14 left-4">
                            <p className="text-muted-foreground text-sm">{UIStrings.ABriefDescriptionOfYourCompany}</p>
                        </div>
                    }
                    contentEditableProps={{
                        id: 'description',
                        onBlur: field.onBlur,
                        name: field.name,
                        className: cn(
                            'overflow-y-auto flex-1 max-h-50',
                            errors.description ? 'border-destructive' : '',
                        ),
                    }}
                />
                <FieldError className="mt-1">{errors.description?.message}</FieldError>
            </div>
        </Field>
    );
};

export default ClientDescription;
